/**
 * Preserve MathType fence brackets (square, round, etc.) through save/load.
 *
 * Why this exists:
 * - editor.getData() / Wiris can drop mfenced open/close attributes.
 * - On reload, Wiris htmlSanitize (DOMPurify) strips those attributes again;
 *   bare <mfenced> then defaults to round parentheses.
 *
 * Save: write each formula from the live CKEditor MathType model (source of truth).
 * Load: wrap <math> as ck-math-widget + data-mathml so upcast skips htmlSanitize.
 *
 * Do NOT convert one bracket type into another — keep whatever was in the model.
 */

const WIRIS_IMAGE_CLASS = "Wirisformula";

const WIRIS_SAFE = {
  tagOpener: "\u00AB",
  tagCloser: "\u00BB",
  doubleQuote: "\u00A8",
  ampersand: "\u00A7",
  quote: "`",
};

const XML_CHARS = {
  tagOpener: "<",
  tagCloser: ">",
  doubleQuote: '"',
  ampersand: "&",
  quote: "'",
};

const replaceAll = (str, from, to) => str.split(from).join(to);

const decodeHtmlEntities = (value) => {
  if (!value) return value;
  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#91;/g, "[")
    .replace(/&#93;/g, "]")
    .replace(/&#x005B;/gi, "[")
    .replace(/&#x005D;/gi, "]")
    .replace(/&amp;/g, "&");
};

const decodeWirisSafeXml = (value) => {
  if (!value) return value;

  let result = decodeHtmlEntities(value);
  result = replaceAll(result, WIRIS_SAFE.tagOpener, XML_CHARS.tagOpener);
  result = replaceAll(result, WIRIS_SAFE.tagCloser, XML_CHARS.tagCloser);
  result = replaceAll(result, WIRIS_SAFE.doubleQuote, XML_CHARS.doubleQuote);
  result = replaceAll(result, WIRIS_SAFE.ampersand, XML_CHARS.ampersand);
  result = replaceAll(result, WIRIS_SAFE.quote, XML_CHARS.quote);

  return result;
};

const encodeWirisSafeXml = (value) => {
  if (!value) return value;

  let result = value;
  result = replaceAll(result, XML_CHARS.tagOpener, WIRIS_SAFE.tagOpener);
  result = replaceAll(result, XML_CHARS.tagCloser, WIRIS_SAFE.tagCloser);
  result = replaceAll(result, XML_CHARS.doubleQuote, WIRIS_SAFE.doubleQuote);
  result = replaceAll(result, XML_CHARS.ampersand, WIRIS_SAFE.ampersand);
  result = replaceAll(result, XML_CHARS.quote, WIRIS_SAFE.quote);

  return result;
};

const ensureMathRoot = (mathml) => {
  const trimmed = (mathml || "").trim();
  if (/^<math[\s>]/i.test(trimmed)) {
    return trimmed;
  }
  return `<math xmlns="http://www.w3.org/1998/Math/MathML">${trimmed}</math>`;
};

/** Collect each mathml formula once (ignore element-end walker visits). */
const collectFormulasFromEditor = (editor) => {
  if (!editor?.model?.document) return [];

  const formulas = [];
  const root = editor.model.document.getRoot();
  if (!root) return formulas;

  const range = editor.model.createRangeIn(root);

  for (const value of range.getWalker({ ignoreElementEnd: true })) {
    const { item } = value;
    if (item.is?.("element", "mathml")) {
      formulas.push(item.getAttribute("formula") || "");
    }
  }

  return formulas;
};

const hasMfenced = (mathml) => /<mfenced\b/i.test(mathml || "");

const hasMathVariant = (mathml) => /\bmathvariant\s*=/i.test(mathml || "");

const needsProtectedMathWidget = (mathml) => hasMfenced(mathml) || hasMathVariant(mathml);

const mathBlockToEditorWidget = (mathBlock) => {
  const math = ensureMathRoot(decodeWirisSafeXml(mathBlock));
  const encoded = encodeWirisSafeXml(math);

  return `<span class="ck-math-widget"><img align="middle" class="${WIRIS_IMAGE_CLASS}" data-mathml="${encoded}" role="math" /></span>`;
};

/**
 * Replace each <math> in getData() HTML with the matching model formula.
 * This keeps square, round, and other fences exactly as they were while editing.
 */
const applyModelFormulasToSaveHtml = (html, formulas) => {
  if (!html || typeof html !== "string") return html;
  if (!formulas.length) return html;

  let index = 0;

  return html.replace(/<math\b[\s\S]*?<\/math>/gi, (mathBlock) => {
    const modelFormula = formulas[index];
    index += 1;

    if (!modelFormula) {
      return mathBlock;
    }

    return ensureMathRoot(decodeWirisSafeXml(modelFormula));
  });
};

/** Use when saving — pass the live CKEditor instance. */
export const preserveMathSquareBrackets = (html, editor = null) => {
  if (editor) {
    const formulas = collectFormulasFromEditor(editor);
    return applyModelFormulasToSaveHtml(html, formulas);
  }
  return html;
};

/**
 * Use when loading HTML into CKEditor (API fetch or post-save remount).
 * Wraps mfenced formulas as data-mathml widgets so open/close survive Wiris sanitize.
 */
export const prepareMathHtmlForEditor = (html) => {
  if (!html || typeof html !== "string") return html;

  return html.replace(/<math\b[\s\S]*?<\/math>/gi, (mathBlock) => {
    const decoded = decodeWirisSafeXml(mathBlock);
    if (needsProtectedMathWidget(decoded)) {
      return mathBlockToEditorWidget(decoded);
    }
    return decoded;
  });
};

/**
 * Use when rendering HTML outside CKEditor (overview / MathJax preview).
 * Converts Wirisformula data-mathml widgets back into real <math> MathML.
 */
export const prepareMathHtmlForDisplay = (html) => {
  if (!html || typeof html !== "string") return html || "";

  let result = html;

  // Full ck-math-widget wrappers from prepareMathHtmlForEditor
  result = result.replace(
    /<span\b[^>]*ck-math-widget[^>]*>[\s\S]*?<\/span>/gi,
    (span) => {
      const match = span.match(/data-mathml=(["'])([\s\S]*?)\1/i);
      if (!match?.[2]) return span;
      return ensureMathRoot(decodeWirisSafeXml(match[2]));
    }
  );

  // Bare Wirisformula images (any attribute order)
  result = result.replace(/<img\b[^>]*>/gi, (imgTag) => {
    if (!/Wirisformula/i.test(imgTag)) return imgTag;
    const match = imgTag.match(/data-mathml=(["'])([\s\S]*?)\1/i);
    if (!match?.[2]) return imgTag;
    return ensureMathRoot(decodeWirisSafeXml(match[2]));
  });

  // Normalize remaining MathML that may still use Wiris safe encoding
  result = result.replace(/<math\b[\s\S]*?<\/math>/gi, (mathBlock) =>
    ensureMathRoot(decodeWirisSafeXml(mathBlock))
  );

  return result;
};

/** @deprecated Use prepareMathHtmlForEditor */
export const restoreMathSquareBrackets = prepareMathHtmlForEditor;
